import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './SendNotification.css';
import { api } from '../config/api';

// Helper function to format date to 'YYYY-MM-DDTHH:mm' local string
const formatLocalDateToDatetimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Notification type enum to match backend
enum NotificationType {
    ALERT = 'ALERT',
    ERROR = 'ERROR',
    VIOLATION = 'VIOLATION',
    MAINTENANCE = 'MAINTENANCE',
    OCCUPANCY = 'OCCUPANCY'
}

// User role enum to match backend's accepted values
enum UserRoles {
    STAFF = 'Staff',
    OWNER = 'Owner',
    OPERATOR = 'Operator',
    USER = 'User',
    SUPER_ADMIN = 'SuperAdmin'
}

interface SendNotificationProps {
    isOpen: boolean;
    onClose: () => void;
}

const SendNotification: React.FC<SendNotificationProps> = ({ isOpen, onClose }) => {
    const { lotId } = useParams<{ lotId: string }>();
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        location: '',
        notificationTimestamp: formatLocalDateToDatetimeLocal(new Date()),
        notificationType: NotificationType.ALERT,
    });
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Convert the local datetime string back to ISO string for the API
        const notificationTimestampISO = new Date(formData.notificationTimestamp).toISOString();

        const notificationData = {
            ...formData,
            lotId,
            read: false,
            visibleBy: [UserRoles.OPERATOR],
            createdBy: 'SuperAdmin',
            notificationTimestamp: notificationTimestampISO,
        };

        setIsLoading(true);

        try {
            const response = await api.post('/mvpnotifications/create-notification', notificationData);

            const data = response.data;

            if (data.success) {
                alert('Notification created successfully!');
                onClose();
                // Reset form with current local time
                setFormData({
                    title: '',
                    message: '',
                    location: '',
                    notificationTimestamp: formatLocalDateToDatetimeLocal(new Date()),
                    notificationType: NotificationType.ALERT,
                });
            } else {
                alert('Failed to create notification: ' + (data.message || 'Unknown error'));
            }
        } catch (error: any) {
            console.error('Error creating notification:', error);
            let errorMessage = 'Failed to create notification';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="notification-modal-content">
                <div className="modal-header">
                    <h2>Send Notification</h2>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="title">Title</label>
                        <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea
                            id="message"
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="location">Location</label>
                        <input
                            type="text"
                            id="location"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="notificationTimestamp">Timestamp</label>
                        <input
                            type="datetime-local"
                            id="notificationTimestamp"
                            value={formData.notificationTimestamp}
                            onChange={(e) => setFormData({ ...formData, notificationTimestamp: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="notificationType">Notification Type</label>
                        <select
                            id="notificationType"
                            value={formData.notificationType}
                            onChange={(e) => setFormData({ ...formData, notificationType: e.target.value as NotificationType })}
                            required
                        >
                            {Object.values(NotificationType).map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onClose}>Cancel</button>
                        <button type="submit">Send Notification</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SendNotification;
